import React, { useEffect, useState } from 'react';
import { Trophy, Loader2, Save } from 'lucide-react';
import { api } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const DIVISIONS = ['7U', '9U', '11U', '13U'];

export const ChampionsBannerControl = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/champions-banner').then((r) => setForm(r.data)).catch(() => setForm({ enabled: false, title: '2026 Division 2 Champions', subtitle: '', divisions: [] })); }, []);
  if (!form) return null;

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/champions-banner', next);
      setForm(data);
      toast({ title: data.enabled ? 'Champions banner is ON' : 'Champions banner is off', description: data.enabled ? 'It now shows at the top of the home page.' : 'Hidden from the home page.' });
    } catch (e) {
      toast({ title: 'Could not save banner', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };
  const toggleDivision = (d) => setForm({ ...form, divisions: form.divisions.includes(d) ? form.divisions.filter((x) => x !== d) : [...form.divisions, d].sort((a, b) => DIVISIONS.indexOf(a) - DIVISIONS.indexOf(b)) });

  return (
    <div className="bg-white border border-[#0a1f44]/10 border-t-4 border-t-[#f4b400] p-5 space-y-4" data-testid="champions-banner-control">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#f4b400] text-[#0a1f44] flex items-center justify-center"><Trophy className="w-5 h-5" aria-hidden="true" /></div>
          <div>
            <div className="font-heading uppercase tracking-[0.3em] text-[10px] text-[#c8102e]">Home page</div>
            <div className="font-display text-xl leading-none">Champions banner</div>
          </div>
        </div>
        <button type="button" role="switch" aria-checked={form.enabled} disabled={saving} onClick={() => save({ ...form, enabled: !form.enabled })} data-testid="champions-banner-toggle" className={`min-h-11 px-5 font-heading uppercase tracking-widest text-[11px] transition-colors disabled:opacity-50 ${form.enabled ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-[#0a1f44] text-white hover:bg-[#c8102e]'}`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : form.enabled ? 'ON — tap to hide' : 'OFF — switch on when we win'}
        </button>
      </div>
      <label className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/60">Headline
        <input value={form.title} maxLength={80} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="champions-banner-title-input" className="mt-1 w-full border border-[#0a1f44]/20 px-3 py-2 text-base font-sans normal-case tracking-normal text-[#0a1f44]" />
      </label>
      <label className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/60">Message
        <textarea value={form.subtitle} maxLength={240} rows={2} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="champions-banner-subtitle-input" className="mt-1 w-full border border-[#0a1f44]/20 px-3 py-2 text-base font-sans normal-case tracking-normal text-[#0a1f44]" />
      </label>
      <div>
        <div className="text-xs font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-2">Which teams won?</div>
        <div className="flex flex-wrap gap-2">
          {DIVISIONS.map((d) => (
            <button key={d} type="button" aria-pressed={form.divisions.includes(d)} onClick={() => toggleDivision(d)} data-testid={`champions-banner-division-${d}`} className={`min-h-10 px-4 font-display text-lg border transition-colors ${form.divisions.includes(d) ? 'bg-[#0a1f44] text-[#f4b400] border-[#0a1f44]' : 'bg-white text-[#0a1f44] border-[#0a1f44]/20 hover:border-[#0a1f44]'}`}>{d}</button>
          ))}
        </div>
      </div>
      <button type="button" disabled={saving} onClick={() => save(form)} data-testid="champions-banner-save" className="min-h-11 inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#a50d26] text-white font-heading uppercase tracking-widest text-[11px] px-5 transition-colors disabled:opacity-50"><Save className="w-4 h-4" aria-hidden="true" /> Save wording</button>
    </div>
  );
};
